<script setup lang="ts">
import type { TextareaProps } from "./textarea.types";
import { useTextarea } from "./useTextarea";

defineOptions({ name: "ETextarea", inheritAttrs: false });

const props = withDefaults(defineProps<TextareaProps>(), {
  color: "neutral",
  variant: "subtle",
  size: "md",
  block: false,
  disabled: false,
  readonly: false,
  required: false,
  autofocusDelay: 0,
  resize: "vertical",
});

const { classes, textareaRef, currentValue, handleInput, handleChange, handleBlur } = useTextarea(props);
</script>

<template>
  <e-primitive
    as="div"
    exportparts="base,input"
    :inner-class="classes.base"
    class="e-textarea"
    :data-disabled="disabled"
    @click="textareaRef?.focus()"
  >
    <textarea
      ref="textareaRef"
      :class="classes.input"
      class="e-textarea-input"
      part="input"
      :value="currentValue"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :rows="rows"
      :style="{ resize }"
      v-bind="$attrs"
      @input="handleInput"
      @change="handleChange"
      @blur="handleBlur"
    />
  </e-primitive>
</template>

<style>
:host {
  display: block;
}

/* Keep the textarea block so the native resize handle can grow it, and bail
   out of the wrapper's `items-center` (copied from input.styles for single-
   line inputs) so the wrapper grows with the textarea as it's resized. */
.e-textarea {
  align-items: stretch;
}

.e-textarea-input {
  display: block;
}
</style>
